import { useMemo, useState } from "react";
import { Form, Formik, Field, FormikProps, FieldInputProps } from "formik";
import * as Yup from "yup";
import useYupHelper from "@/features/ui/form/use-yup-helper";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Text,
} from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import SignUpBusinessRegistrationField from "@/features/auth/sign-up/sign-up-business-registration-field";
import useParseBusinessRegistrationCertificate from "@/shared/hooks/business-registration-certificate/useParseBusinessRegistrationCertificate";
import {
  BUSINESS_REGISTRATION_NUMBER_PATTERN,
  formatBusinessRegistrationNumber,
} from "@/shared/constants/business-registration-certificate";
import { REFERRAL_MAX_LENGTH } from "@/features/auth/sign-up/sign-up-referral";

export interface SignUpStep3FormFields {
  businessRegistrationFile: File | null;
  // 업로드된 파일의 uno_file_id. 값이 있으면 파싱까지 끝난 상태라
  // 이 값 하나로 나머지 필드 노출 여부를 판단한다(뒤로 돌아와도 복원된다).
  businessRegistrationCertificateFileId: number | null;
  // 파싱 API가 실패(OCR 토큰 부족 등)하면 파일 없이 직접 입력하도록 전환한다.
  // fileId와 마찬가지로 폼 값에 둬서 뒤로 돌아와도 직접 입력 상태가 복원된다.
  isBusinessRegistrationManualEntry: boolean;
  businessRegistrationNumber: string;
  userInputedCompanyName: string;
  name: string;
  // 선택 입력. 가입 URL 의 ?ref= 로 들어오면 미리 채워지고, 그대로 고치거나 지울 수 있다.
  recommender: string;
}

interface SignUpStep3FormProps {
  initialValue: SignUpStep3FormFields;
  onSubmit: (formValues: SignUpStep3FormFields) => void;
}

export default function SignUpStep3Form({
  initialValue,
  onSubmit,
}: SignUpStep3FormProps) {
  const { tTitle, tSentence } = useI18nHelper();
  const { withMessage, formT } = useYupHelper({
    prefix: "sign_up_form",
    ns: "auth",
  });
  const { parseBusinessRegistrationCertificate, isPending: isParsing } =
    useParseBusinessRegistrationCertificate();

  // 파일 관련 안내는 Formik 에러에 두면 다음 검증에서 required 메시지로 덮이므로 따로 들고 있는다.
  const [fileError, setFileError] = useState<string | null>(null);
  const [hasMissingFields, setHasMissingFields] = useState(false);

  const validateSchema = useMemo(() => {
    // 직접 입력 모드에선 첨부·파싱된 파일이 없으므로 파일 필수 조건을 푼다.
    const requiredUnlessManualEntry = {
      is: true,
      then: <T extends Yup.Schema>(schema: T) => schema.notRequired(),
      otherwise: <T extends Yup.Schema>(schema: T) =>
        schema.required(withMessage.required),
    };

    return Yup.object({
      businessRegistrationFile: Yup.mixed<File>()
        .label("business_registration_certificate")
        .nullable()
        .when("isBusinessRegistrationManualEntry", requiredUnlessManualEntry),
      businessRegistrationCertificateFileId: Yup.number()
        .label("business_registration_certificate")
        .nullable()
        .when("isBusinessRegistrationManualEntry", requiredUnlessManualEntry),
      businessRegistrationNumber: Yup.string()
        .required(withMessage.required)
        .matches(BUSINESS_REGISTRATION_NUMBER_PATTERN, {
          message: formT("field.business_registration_number.error.invalid"),
          excludeEmptyString: true,
        }),
      userInputedCompanyName: Yup.string().required(withMessage.required),
      name: Yup.string().label("full_name").required(withMessage.required),
      recommender: Yup.string().max(REFERRAL_MAX_LENGTH, withMessage.max),
    });
  }, [withMessage.max, withMessage.required, formT]);

  // 파일을 고르면 파싱 API를 호출해 나머지 필드를 채운다.
  // 성공하면 fileId가 채워지고, 실패하면 직접 입력 모드로 바뀌며, 둘 중 하나가 되어야 나머지 필드가 노출된다.
  const handleFileSelect = async (
    file: File,
    form: FormikProps<SignUpStep3FormFields>
  ) => {
    setFileError(null);
    setHasMissingFields(false);
    form.setFieldTouched("businessRegistrationFile", true, false);
    form.setValues((prev) => ({
      ...prev,
      businessRegistrationFile: file,
      // 파일을 새로 고르면 이전 파싱 결과는 무효다.
      businessRegistrationCertificateFileId: null,
    }));

    try {
      const { unoFileId, result } = await parseBusinessRegistrationCertificate(
        file
      );
      setHasMissingFields(result.missingRequiredFields.length > 0);
      form.setValues((prev) => ({
        ...prev,
        businessRegistrationFile: file,
        businessRegistrationCertificateFileId: unoFileId,
        isBusinessRegistrationManualEntry: false,
        // 응답 필드는 모두 nullable이라 못 읽은 항목은 빈 값으로 두고 사용자가 채운다.
        businessRegistrationNumber: result.businessRegistrationNumber ?? "",
        userInputedCompanyName: result.companyName ?? "",
        // 담당자명(name)은 사업자등록증상의 대표자와 다를 수 있어 자동으로 채우지 않는다.
      }));
    } catch {
      // 이 화면(PublicLayout)에는 토스트 컨테이너가 없어 실패는 인라인으로만 알린다.
      // 파일은 저장되지 않았으므로 파일명을 남기지 않고, 가입은 막지 않도록 직접 입력 모드로 넘긴다.
      // 이미 입력해 둔 사업자 정보는 그대로 둔다.
      form.setValues((prev) => ({
        ...prev,
        businessRegistrationFile: null,
        businessRegistrationCertificateFileId: null,
        isBusinessRegistrationManualEntry: true,
      }));
    }
  };

  return (
    <Formik
      initialValues={initialValue}
      validateOnChange={true}
      validationSchema={validateSchema}
      onSubmit={(values, actions) => {
        onSubmit(values);
      }}
    >
      {(props: FormikProps<SignUpStep3FormFields>) => {
        const isParsed =
          props.values.businessRegistrationCertificateFileId !== null;
        const isManualEntry = props.values.isBusinessRegistrationManualEntry;
        const showDetailFields = isParsed || isManualEntry;

        return (
          <Form>
            <Box display="flex" flexDirection="column" gap="32px">
              <Box
                display="flex"
                flexDirection="column"
                gap="16px"
                className="flex flex-col gap-4"
              >
                <Field name="businessRegistrationFile">
                  {({
                    field,
                    form,
                  }: {
                    field: FieldInputProps<File | null>;
                    form: FormikProps<SignUpStep3FormFields>;
                  }) => (
                    <FormControl
                      isRequired
                      isInvalid={
                        !!fileError ||
                        (!!form.errors.businessRegistrationFile &&
                          !!form.touched.businessRegistrationFile)
                      }
                    >
                      <FormLabel display="flex" m={"0"} paddingBottom={"4px"}>
                        <Text
                          color="#6F6F6F"
                          fontSize={"12px"}
                          fontWeight={"400"}
                        >
                          {tTitle(
                            "auth:sign_up_form.field.business_registration_certificate"
                          )}
                        </Text>
                      </FormLabel>
                      <SignUpBusinessRegistrationField
                        value={field.value}
                        isLoading={isParsing}
                        onTouch={() => {
                          form.setFieldTouched(field.name, true);
                        }}
                        onChange={(file) => {
                          if (file) {
                            handleFileSelect(file, form);
                          }
                        }}
                        onInvalidType={() => {
                          form.setFieldTouched(field.name, true, false);
                          setFileError(
                            tSentence(
                              "auth:sign_up_form.field.business_registration_certificate.error.invalid_type"
                            )
                          );
                        }}
                        onTooLarge={() => {
                          form.setFieldTouched(field.name, true, false);
                          setFileError(
                            tSentence(
                              "auth:sign_up_form.field.business_registration_certificate.error.too_large"
                            )
                          );
                        }}
                      />
                      <Flex
                        h="24px"
                        flexDirection="row"
                        alignItems={"center"}
                        marginTop={"8px"}
                      >
                        {fileError ? (
                          <FormErrorMessage m="0" fontSize={"12px"}>
                            {fileError}
                          </FormErrorMessage>
                        ) : hasMissingFields ? (
                          <Text color="#DE7000" fontSize={"12px"}>
                            {tSentence(
                              "auth:sign_up_form.field.business_registration_certificate.warning.partial"
                            )}
                          </Text>
                        ) : isManualEntry ? (
                          <Text color="#DE7000" fontSize={"12px"}>
                            {tSentence(
                              "auth:sign_up_form.field.business_registration_certificate.warning.parse_failed"
                            )}
                          </Text>
                        ) : (
                          <Text color="#8F8F8C" fontSize={"12px"}>
                            {tSentence(
                              "auth:sign_up_form.field.business_registration_certificate.description"
                            )}
                          </Text>
                        )}
                      </Flex>
                    </FormControl>
                  )}
                </Field>
                {/* 파싱이 끝나기 전에는 채울 근거가 없어 아래 필드들을 감춘다(파싱 실패 시엔 직접 입력용으로 노출). */}
                {showDetailFields && (
                  <>
                    <Field name="businessRegistrationNumber">
                      {({
                        field,
                        form,
                      }: {
                        field: FieldInputProps<string>;
                        form: FormikProps<SignUpStep3FormFields>;
                      }) => (
                        <FormControl
                          isRequired
                          isInvalid={
                            !!form.errors.businessRegistrationNumber &&
                            form.touched.businessRegistrationNumber
                          }
                        >
                          <FormLabel display="flex" m={"0"}>
                            <Text
                              color="#6F6F6F"
                              fontSize={"12px"}
                              fontWeight={"400"}
                            >
                              {tTitle(
                                "auth:sign_up_form.field.business_registration_number"
                              )}
                            </Text>
                          </FormLabel>
                          <Input
                            {...field}
                            fontSize={"12px"}
                            inputMode={"numeric"}
                            maxLength={12}
                            placeholder={tTitle(
                              "auth:sign_up_form.field.business_registration_number.placeholder"
                            )}
                            onChange={(event) => {
                              form.setFieldValue(
                                field.name,
                                formatBusinessRegistrationNumber(
                                  event.target.value
                                )
                              );
                            }}
                          />
                          <Flex
                            h="24px"
                            flexDirection="row"
                            alignItems={"center"}
                          >
                            <FormErrorMessage m="0" fontSize={"12px"}>
                              {form.errors.businessRegistrationNumber}
                            </FormErrorMessage>
                          </Flex>
                        </FormControl>
                      )}
                    </Field>
                    <Field name="userInputedCompanyName">
                      {({
                        field,
                        form,
                      }: {
                        field: FieldInputProps<string>;
                        form: FormikProps<SignUpStep3FormFields>;
                      }) => (
                        <FormControl
                          isRequired
                          isInvalid={
                            !!form.errors.userInputedCompanyName &&
                            form.touched.userInputedCompanyName
                          }
                        >
                          <FormLabel display="flex" m={"0"}>
                            <Text
                              color="#6F6F6F"
                              fontSize={"12px"}
                              fontWeight={"400"}
                            >
                              {tTitle(
                                "auth:sign_up_form.field.user_inputed_company_name",
                                {
                                  ns: "auth",
                                }
                              )}
                            </Text>
                          </FormLabel>
                          <Input
                            {...field}
                            fontSize={"12px"}
                            placeholder={tTitle(
                              "auth:sign_up_form.field.user_inputed_company_name.placeholder"
                            )}
                          />
                          <Flex
                            h="24px"
                            flexDirection="row"
                            alignItems={"center"}
                          >
                            <FormErrorMessage m="0" fontSize={"12px"}>
                              {form.errors.userInputedCompanyName}
                            </FormErrorMessage>
                          </Flex>
                        </FormControl>
                      )}
                    </Field>
                    <Field name="name">
                      {({
                        field,
                        form,
                      }: {
                        field: FieldInputProps<string>;
                        form: FormikProps<SignUpStep3FormFields>;
                      }) => (
                        <FormControl
                          isRequired
                          isInvalid={!!form.errors.name && form.touched.name}
                        >
                          <FormLabel display="flex" m={"0"}>
                            <Text
                              color="#6F6F6F"
                              fontSize={"12px"}
                              fontWeight={"400"}
                            >
                              {tTitle("sign_up_form.field.full_name", {
                                ns: "auth",
                              })}
                            </Text>
                          </FormLabel>
                          <Input
                            {...field}
                            fontSize={"12px"}
                            placeholder={tTitle(
                              "auth:sign_up_form.field.full_name.placeholder"
                            )}
                          />
                          <Flex
                            h="24px"
                            flexDirection="row"
                            alignItems={"center"}
                          >
                            <FormErrorMessage m="0" fontSize={"12px"}>
                              {form.errors.name}
                            </FormErrorMessage>
                          </Flex>
                        </FormControl>
                      )}
                    </Field>
                    <Field name="recommender">
                      {({
                        field,
                        form,
                      }: {
                        field: FieldInputProps<string>;
                        form: FormikProps<SignUpStep3FormFields>;
                      }) => (
                        <FormControl
                          isInvalid={
                            !!form.errors.recommender &&
                            form.touched.recommender
                          }
                        >
                          <FormLabel display="flex" m={"0"}>
                            <Text
                              color="#6F6F6F"
                              fontSize={"12px"}
                              fontWeight={"400"}
                            >
                              {tTitle("auth:sign_up_form.field.recommender")}
                            </Text>
                          </FormLabel>
                          <Input
                            {...field}
                            fontSize={"12px"}
                            maxLength={REFERRAL_MAX_LENGTH}
                            placeholder={tTitle(
                              "auth:sign_up_form.field.recommender.placeholder"
                            )}
                          />
                          <Flex
                            h="24px"
                            flexDirection="row"
                            alignItems={"center"}
                          >
                            {form.errors.recommender &&
                            form.touched.recommender ? (
                              <FormErrorMessage m="0" fontSize={"12px"}>
                                {form.errors.recommender}
                              </FormErrorMessage>
                            ) : (
                              <Text color="#8F8F8C" fontSize={"12px"}>
                                {tSentence(
                                  "auth:sign_up_form.field.recommender.description"
                                )}
                              </Text>
                            )}
                          </Flex>
                        </FormControl>
                      )}
                    </Field>
                  </>
                )}
              </Box>
              <AppButton
                variant={"gray"}
                size={"large"}
                width={"100%"}
                chakraProps={{ height: "40px" }}
                type={"submit"}
                isDisabled={
                  isParsing ||
                  !(
                    props.isValid &&
                    showDetailFields &&
                    props.values.businessRegistrationNumber !== "" &&
                    props.values.userInputedCompanyName !== "" &&
                    props.values.name !== ""
                  )
                }
                isLoading={props.isSubmitting}
              >
                <Text fontSize={"14px"}>
                  {tTitle("auth:sign_up_form.button.next.do")}
                </Text>
              </AppButton>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
}
